import {
  RESTRICTED_SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_LABELS,
} from '../constants/subscription';
import {
  // NO_VALID_ORG_LICENSE,
  getSubscriptionStatusMessage,
  // GUEST_LICENSE,
  getSFOrgStatusMessage,
} from '../constants/texts';
// import { OrganizationLicense } from '../enums/account';
import {
  SubscriptionData,
  SubscriptionInfo,
  SubscriptionStatus,
} from '../interfaces/subscription';
import { UserRole } from '../enums/userRoles';
import { Notify } from './notificationService';
import { axiosClient } from '../api/axiosClient';
import { trackException } from '../utils/monitoring';
import { AxiosError } from 'axios';
import { AppRoutes } from '../constants/route';
import store, { RootState } from '../app/store';
import Stripe from 'stripe';

let isReadonly = false;
let isAdminAgentReadonly = false;
let readonlyTitle = '';
let subscriptionInfo: SubscriptionInfo | null = null;

export const getIsReadonly = (): boolean => {
  return isReadonly;
};

export { isAdminAgentReadonly };

export const setSubscriptionInfo = (info: SubscriptionInfo): void => {
  subscriptionInfo = info;
};

export const getCirraUserId = (): string | null => {
  return subscriptionInfo?.cirraUserId || null;
};

export const getCirraOrganizationId = (): string | null => {
  return subscriptionInfo?.cirraOrganizationId || null;
};

// Returns the internal Cirra AI ID (the Mongo record ID) for the organization
export const getOrgId = (): string | null => {
  return subscriptionInfo?.organizationId || null;
};

// Returns the internal Cirra AI ID (the Mongo record ID) for the user
export const getUserId = (): string | null => {
  return subscriptionInfo?.userId || null;
};

/*
export const getLicense = (): OrganizationLicense => {
  return subscriptionInfo?.license as OrganizationLicense;
};
*/

export const getRole = (): UserRole => {
  return subscriptionInfo?.role as UserRole;
};

export const getRoleName = (): string => {
  const role = getRole();
  return (role || '').replace('Company ', '');
};

export const getStripeCustomerId = (): string | null => {
  return subscriptionInfo?.stripeCustomerId || null;
};

export const getSubscriptionStatusReadOnly = (
  status: string | undefined
): boolean => {
  isReadonly = true;
  const restrictedSubscription =
    !status ||
    RESTRICTED_SUBSCRIPTION_STATUSES.includes(
      status?.toLowerCase() as SubscriptionStatus
    );

  if (restrictedSubscription) {
    readonlyTitle = getSubscriptionStatusMessage(status as SubscriptionStatus);
    /*
  } else if (isTeamUser()) {
    const orgLicense = getLicense();
    if (!orgLicense && status !== SubscriptionStatus.FREE) {
      readonlyTitle = NO_VALID_ORG_LICENSE;
    } else if (orgLicense === OrganizationLicense.Guest) {
      readonlyTitle = GUEST_LICENSE;
    } else {
      isReadonly = false;
    }
  */
  } else {
    isReadonly = false;
  }
  return isReadonly;
};

export const isUserReadonly = (status: string | undefined): boolean => {
  readonlyTitle = '';
  isReadonly = false;
  isAdminAgentReadonly = false;

  if (getSubscriptionStatusReadOnly(status)) {
    isAdminAgentReadonly = true;
    return isReadonly;
  }

  const selectedOrgCredential = store.getState().organization.org;

  if (!selectedOrgCredential?.active) {
    readonlyTitle = getSFOrgStatusMessage(selectedOrgCredential?.status);
    isReadonly = true;
    return isReadonly;
  }

  isReadonly = false;
  return isReadonly;
};

export const hasPersonalLicense = (): boolean => {
  const { isPersonalUser } = (store.getState() as RootState).access;
  return isPersonalUser;
};

// Note: This function does not trigger re-renders when the state changes
// Prefer `useUserAccess()` hook instead to avoid the stale state issue
export const isTeamUser = (): boolean => {
  return (store.getState() as RootState).access.isTeamUser;
};

export const getReadonlyElementTitle = (title?: string): string | undefined => {
  return isReadonly ? readonlyTitle : title;
};

export const getSubscriptionStatusLabel = (
  status?: string | SubscriptionStatus
): string => {
  // Convert enum to string if needed, then to lowercase for lookup
  const statusString = typeof status === 'string' ? status : status;
  return (
    SUBSCRIPTION_LABELS[
      statusString?.toLowerCase() as keyof typeof SUBSCRIPTION_LABELS
    ] ?? SUBSCRIPTION_LABELS[SubscriptionStatus.UNKNOWN]
  );
};

const _generateSubscriptionPortalUrl = async (
  flowType?: 'activate_trial',
  priceId?: string
): Promise<string> => {
  try {
    const body: Record<string, string> = {};
    if (flowType) body.flow_type = flowType;
    if (priceId) body.price_id = priceId;

    const { data } = await axiosClient.post<{ url: string }>(
      `/subscriptions/portal-session`,
      Object.keys(body).length > 0 ? body : undefined
    );

    const sessionUrl = data?.url;
    if (!sessionUrl?.includes('billing.stripe.com')) {
      trackException(
        new Error('Invalid subscription portal URL:' + sessionUrl)
      );
      throw new Error('Invalid subscription portal URL');
    }
    return sessionUrl;
  } catch (error) {
    const axiosError = error as AxiosError;

    // Handle unauthorized error specifically
    if (axiosError.response?.status === 401) {
      throw new Error('Please log in again to manage your subscription');
    }

    // Handle customer not found error
    const errorData = axiosError.response?.data as { message?: string };
    if (
      errorData?.message === 'Customer not found' ||
      errorData?.message?.includes('Stripe customer ID')
    ) {
      throw new Error(
        'Unable to find your subscription. Please contact support@cirra.ai.'
      );
    }

    throw error;
  }
};

// Returns true if the subscription portal was opened successfully, false otherwise
export const openSubscriptionPortal = async (
  flowType?: 'activate_trial',
  priceId?: string
): Promise<boolean> => {
  try {
    const sessionUrl = await _generateSubscriptionPortalUrl(flowType, priceId);
    window.open(sessionUrl, '_blank');
    return true;
  } catch (error) {
    Notify.error(
      (error as Error).message || 'Failed to open subscription portal'
    );
    return false;
  }
};

// Returns true if the subscription portal was opened successfully, false otherwise
export const openPricingPage = (): boolean => {
  window.open(AppRoutes.pricing.path, '_blank');
  return true;
};

export const openPricingPageOrSubscriptionPortal = async (
  subscriptionStatus: SubscriptionStatus,
  flowType?: 'activate_trial',
  priceId?: string
): Promise<boolean> => {
  // FREE users don't have a Stripe customer and can't open the portal unless
  // a flowType is explicitly provided (e.g. activate_trial to add payment method).
  const shouldOpenSubscriptionPortal =
    [
      SubscriptionStatus.ACTIVE,
      SubscriptionStatus.INACTIVE,
      SubscriptionStatus.TRIALING,
      SubscriptionStatus.PAUSED,
      SubscriptionStatus.PAST_DUE,
      SubscriptionStatus.EXPIRED,
    ].includes(subscriptionStatus) ||
    (subscriptionStatus === SubscriptionStatus.FREE && !!flowType);

  if (shouldOpenSubscriptionPortal) {
    return openSubscriptionPortal(flowType, priceId);
  } else {
    return openPricingPage();
  }
};

/**
 * Redirect to Stripe Checkout for a new subscription.
 * Opens in the same tab so Stripe can redirect back with ?checkout_success=true.
 * See docs/subscription-checkout-flow.md for full flow documentation.
 */
export const openCheckoutForPlan = async (
  priceId: string
): Promise<boolean> => {
  try {
    const { data } = await axiosClient.post<{ url: string }>(
      '/subscriptions/checkout-session',
      { price_id: priceId }
    );

    const checkoutUrl = data?.url;
    if (!checkoutUrl?.includes('checkout.stripe.com')) {
      trackException(new Error('Invalid checkout URL: ' + checkoutUrl));
      throw new Error('Invalid checkout URL');
    }

    window.location.href = checkoutUrl;
    return true;
  } catch (error) {
    Notify.error((error as Error).message || 'Failed to open checkout');
    return false;
  }
};

/**
 * Activate a trial subscription by ending the trial immediately.
 * Should be called after the user adds a payment method via the portal.
 */
export const activateTrialSubscription = async (
  priceId?: string
): Promise<boolean> => {
  try {
    const body = priceId ? { price_id: priceId } : undefined;
    const { data } = await axiosClient.post<{ status: string }>(
      '/subscriptions/activate-trial',
      body
    );
    return data?.status === 'active';
  } catch (error) {
    trackException(error as Error);
    return false;
  }
};

export const isUserManagementReadonly = (
  status: string | undefined
): boolean => {
  return getSubscriptionStatusReadOnly(status);
};

export const convertSubscriptionDataToStripeSubscription = (
  sub: SubscriptionData | null | undefined
): Pick<Stripe.Subscription, 'customer'> | Record<string, never> => {
  if (!sub) return {};
  return (
    sub.rawData || {
      customer: sub.stripeCustomerId || 'unknown',
    }
  );
};
